import React, { FC } from 'react';
import { useObserver } from 'mobx-react-lite';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SocialLinkProductCard } from './SocialLinkProductCard';
import { KitPriceDisplay } from '../order-selection/common-er/util-er-kit/KitPriceDisplay';
import { ProductInfoModal } from '../product-info/util/ProductInfoModal';
import { AdditionalItemCard } from './AdditionalItemCard';
import { PromoItemCard } from './PromoItemCard';
import { UnavailableItemCard } from './UnavailableItemCard';
import { EnrollmentOrderInfoStore } from './EnrollmentOrderInfoStore';
import { AddProducts } from '../order-selection/add-products/AddProducts';
import { MeoKitSelection } from '../order-selection/minimum-enrollment-order/MeoKitSelection';
import { ItemInfoModal } from '../product-info/util/ItemInfoModal';
import { useRootStore } from '../root/RootStoreHooks';
import { MoneyDisplay } from '../order-selection/starter-kit-selection/util-starter-kit/MoneyDisplay';
import { SocialLinkCustomiseButton } from './SocialLinkCustomiseButton';

export interface EnrollmentOrderInfoProps {
  orderInfo: EnrollmentOrderInfoStore;
  isMeoMarket: boolean;
  minimumERPVReached: boolean;
  minimumEnrollmentOrderPVreached : boolean;
}

export const EnrollmentOrderInfo: FC<EnrollmentOrderInfoProps> = ({ orderInfo, isMeoMarket,minimumERPVReached,minimumEnrollmentOrderPVreached }) => {
  const { t } = useTranslation();
  const rootStore = useRootStore();
  const newSlMode = rootStore.moduleStores.useNewSLMode;

  return useObserver(() => {
    const starterKit = orderInfo.starterKit;
    const additionalItems = orderInfo.additionalItems;
    const unavailableItems = orderInfo.unavailableAdditionalItems.filter(
      (elem, index) => orderInfo.unavailableAdditionalItems.findIndex(obj => obj.legacyId === elem.legacyId) === index
    );
    const erKit = orderInfo.erKit;
    const erAdditionalItems = orderInfo.erAdditionalItems;
    const displayFirstOrder = starterKit || additionalItems.length > 0 || unavailableItems.length > 0;
    const displaySubscription = erKit || erAdditionalItems.length > 0;
    const isErIncludedInFirstOrder = !(
      orderInfo.usePremiumStarterKitAsErOrder || orderInfo.usePremiumStarterKitAsMeoOrder
    );
       return (
      <>
        {displayFirstOrder && (
          <>
            <h5 className="text-primary my-3 text-center">
              <strong>
                {orderInfo.isEnrollingInEr
                  ? t('EnrollmentOrderInfo_YourFirstErOrder')
                  : t('EnrollmentOrderInfo_YourFirstOrder')}
              </strong>
              <div style={{backgroundColor:'lightgreen'}}>
                  {(orderInfo?.voucherCodeDetails && orderInfo?.voucherCodeDetails?.code > 0 && !orderInfo?.voucherCodeDetails?.isExpired) && 
                  (<>{t('SocialLinkEntryVoucher_Content1')} {''}
                  <span> 
                    {orderInfo.voucherCodeDetails.amount && <MoneyDisplay price={orderInfo.voucherCodeDetails.amount} />} {''}
                  </span>
                  {t('SocialLinkEntryVoucher_Content2')} {''}</>)}
            </div>
            </h5>
            <div className="my-3">
              <Row>
                {starterKit && <SocialLinkProductCard kit={starterKit} newSlMode={newSlMode} />}
                {additionalItems
                  .filter(item => !unavailableItems.some(i => i.legacyId === item.legacyId))
                  .map(item =>
                    item.isPromotional ? <PromoItemCard item={item} newSlMode={newSlMode}/> : <AdditionalItemCard item={item} newSlMode={newSlMode} />
                  )}
              </Row>
              <Row>
                {unavailableItems.length > 0 && (
                  <>
                    <h5 className="text-primary my-3 text-center">{t('UnavailableItems')}</h5>
                    <p>{t('UnavailableItemsText')}</p>
                  </>
                )}
                {unavailableItems.map(item => (
                  <UnavailableItemCard item={item} newSlMode={newSlMode}/>
                ))}
              </Row>
              {((unavailableItems.length > 0 && !minimumEnrollmentOrderPVreached) || !minimumEnrollmentOrderPVreached) && (
                <Row className="justify-content-md-center">
                  {!isMeoMarket ? (
                    <Col md="auto" className="my-3">
                      <AddProducts />
                    </Col>
                  ) : (
                    <MeoKitSelection isSocialLink={true} />
                  )}
                </Row>
              )}

            </div>
          </>
        )}
        {displaySubscription && (
          <>
          {orderInfo.isEnrollingInEr && (
            <div
              className="my-3"
              dangerouslySetInnerHTML={{ __html: t('EnrollmentOrderInfo_YourMonthlySubscriptionDescription') }}
            />)}
            <h5 className="text-primary my-3 text-center">
            {orderInfo.isEnrollingInEr && (
              <div className="mt-3">
                <strong>{t('EnrollmentOrderInfo_YourMonthlySubscription')}</strong>
              </div>
              )}
              {isErIncludedInFirstOrder && (
                <div>
                  <strong> {t('EnrollmentOrderInfo_IncludedInFirstOrder')} </strong>
                </div>
              )}
            </h5>
            <div className="my-3">
              <Row>
                {erKit && <SocialLinkProductCard kit={erKit} newSlMode={newSlMode} isSubscription={true}/>}
                {orderInfo.isEnrollingInEr && erAdditionalItems.map(item => (
                  <AdditionalItemCard item={item} newSlMode={newSlMode} isSubscription={true}/>
                ))}
              </Row>
           
            </div>
          </>
        )}
         <div className='d-flex align-items-end flex-column mt-4 mb-4'>
              {((unavailableItems.length > 0 && !minimumERPVReached) ||!minimumERPVReached ) 
              &&  (
                <Row className="justify-content-md-center">
                  {(
                      <SocialLinkCustomiseButton isMeoMarket={isMeoMarket}/>
                  )}
                </Row>
              )}
          </div>
        {orderInfo.productInfo && (
          <ProductInfoModal
            isOpen={orderInfo.showProductModal}
            onClose={() => orderInfo.hideProductInfo()}
            onKitSelected={() => null}
            productInfo={orderInfo.productInfo}
            priceDisplay={<KitPriceDisplay kit={orderInfo.productInfo.kit} />}
            hideSelectButton
          />
        )}
        {orderInfo.itemInfo && (
          <ItemInfoModal
            isOpen={orderInfo.showInfoModal}
            onClose={() => orderInfo.hideItemInfo()}
            itemInfo={orderInfo.itemInfo}
            priceDisplay = {<></>}
          />
        )}
      </>
    );
  });
};
